import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ApiError, dev, get, post } from "../api/client";
import type { HandleCheck, InvitePreview } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { useTurnstile } from "../auth/Turnstile";
import { googleConfigured, signInDev, signInWithGoogle } from "../auth/google";
import { cleanHandleInput, initialsOf } from "../components/HandleSheet";
import { Shell } from "../components/Shell";
import { ErrorLine } from "../components/ui";

const HANDLE_RE = /^[a-z][a-z0-9_]{2,23}$/;

export default function ClaimPage() {
  const { token = "" } = useParams<{ token: string }>();
  const { me, refresh, signOut } = useAuth();
  const nav = useNavigate();

  const [invite, setInvite] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [handle, setHandle] = useState("");
  const [check, setCheck] = useState<HandleCheck | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<Error | null>(null);
  const [needsEmailConfirm, setNeedsEmailConfirm] = useState(false);
  const [emailForbidden, setEmailForbidden] = useState(false);
  const [devOpen, setDevOpen] = useState(false);
  const [devEmail, setDevEmail] = useState("kiran@deepthird.local");
  const [devName, setDevName] = useState("Kiran P.");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    get<InvitePreview>(`/v1/invites/${encodeURIComponent(token)}`)
      .then((res) => {
        if (cancelled) return;
        setInvite(res);
        if (res.state === "pending") {
          const initial = me?.user.handle || (res.handle_available ? res.suggested_handle : res.handle_suggestion || res.suggested_handle);
          setHandle(initial);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) {
          setInvite({ state: "unknown" });
        } else {
          setErr(e as Error);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, me?.user.handle]);

  const verification = useTurnstile(googleConfigured && !me && !loading && invite?.state === "pending");

  const clean = cleanHandleInput(handle);
  useEffect(() => {
    if (!invite || invite.state !== "pending" || !clean) {
      setCheck(null);
      return;
    }
    if (me?.user.handle && clean === me.user.handle) {
      setCheck({ handle: clean, valid: true, available: true, problem: null, message: null, suggestions: [] });
      return;
    }
    if (!HANDLE_RE.test(clean)) {
      setCheck({ handle: clean, valid: false, available: false, problem: "format", message: "3 to 24 lowercase letters, digits, or _", suggestions: [] });
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(() => {
      get<HandleCheck>(`/v1/users/check-handle?handle=${encodeURIComponent(clean)}`)
        .then((r) => {
          if (!cancelled) setCheck(r);
        })
        .catch(() => {});
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [clean, invite, me?.user.handle]);

  const finishClaimRedirect = (res: { team_id: string; video_id: string | null; handle?: string | null | undefined }) => {
    const h = res.handle || me?.user.handle || clean;
    if (res.video_id && h) {
      nav(`/u/${h}/clips?video=${res.video_id}`, { replace: true });
    } else if (h) {
      nav(`/u/${h}?team=${res.team_id}`, { replace: true });
    } else {
      nav(`/teams/${res.team_id}`, { replace: true });
    }
  };

  const doClaim = async (confirmEmailMismatch = false) => {
    if (busy || !clean) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await post<{ team_id: string; role: string; video_id: string | null; handle: string }>(
        `/v1/invites/${encodeURIComponent(token)}/claim`,
        {
          handle: clean,
          ...(confirmEmailMismatch ? { confirm_email_mismatch: true } : {}),
        },
      );
      await refresh();
      finishClaimRedirect(res);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.details?.reason === "CONFIRM_EMAIL_MISMATCH") {
        setNeedsEmailConfirm(true);
      } else if (e instanceof ApiError && e.status === 403 && e.details?.reason === "EMAIL_MISMATCH") {
        setEmailForbidden(true);
      } else {
        setErr(e as Error);
      }
    } finally {
      setBusy(false);
    }
  };

  const onGoogleContinue = async () => {
    if (!googleConfigured) {
      setDevOpen(true);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const res = await signInWithGoogle({ turnstileToken: verification.getToken, inviteToken: token, handle: clean || undefined });
      await refresh();
      const claimed = res.claimed ?? res.invite_claimed;
      if (claimed) {
        finishClaimRedirect({ ...claimed, handle: res.user.handle });
      }
    } catch (e) {
      setErr(e as Error);
    } finally {
      verification.reset();
      setBusy(false);
    }
  };

  const onDevSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await signInDev(devEmail, "free", devName, clean || undefined);
      await refresh();
      setDevOpen(false);
    } catch (e) {
      setErr(e as Error);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Shell brand>
        <div className="p" style={{ paddingTop: 40, maxWidth: 480 }}>
          <p className="sub">Loading invite…</p>
        </div>
      </Shell>
    );
  }

  if (!invite || invite.state !== "pending") {
    const state = invite?.state ?? "unknown";
    const headline =
      state === "claimed"
        ? "This invite has already been claimed."
        : state === "revoked"
          ? "This invite was cancelled."
          : state === "expired"
            ? "This invite has expired."
            : "Invite not found.";
    return (
      <Shell brand>
        <div className="p" style={{ paddingTop: 40, maxWidth: 480 }} data-testid="claim-nonpending">
          <h1>{headline}</h1>
          <p className="sub mt10">Ask your team manager to send a fresh invite link if you still need access.</p>
          <ErrorLine error={err} />
          <div className="mt24">
            <Link to="/" className="btn dark wide">Go to Deep Third</Link>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell brand right={me ? <span className="tiny">Signed in</span> : <Link to="/signin" state={{ from: `/claim/${token}` }}>Sign in</Link>}>
      <div className="p" style={{ paddingTop: 32, paddingBottom: 40, maxWidth: 480 }} data-testid="claim-page">
        <div className="row" style={{ justifyContent: "flex-start", gap: 10 }}>
          <span className="avatar blue">{initialsOf(invite.team.name)}</span>
          <div>
            <div className="med">{invite.team.name}</div>
            {invite.invited_by && (
              <div className="tiny">
                Invited by {invite.invited_by.display_name}
                {invite.invited_by.handle ? ` (@${invite.invited_by.handle})` : ""}
              </div>
            )}
          </div>
        </div>

        <h1 className="mt20" style={{ fontSize: 28 }}>
          {invite.match
            ? `${invite.match.clip_count} ${invite.match.clip_count === 1 ? "clip is" : "clips are"} waiting for you.`
            : `Join ${invite.team.name} on Deep Third.`}
        </h1>
        <p className="sub mt8">
          {invite.match ? (
            <>
              Assigned to <b>@{invite.suggested_handle}</b> in <b>{invite.match.title}</b>. Watch and download every delivery free.
            </>
          ) : (
            <>
              Invited as <b>{invite.role}</b> with handle <b>@{invite.suggested_handle}</b>.
            </>
          )}
        </p>

        {!me ? (
          <>
            <div className="notice mt20">
              <div className="tiny">Your handle</div>
              <div className="row mt4">
                <span className="h2" style={{ fontSize: 18 }}>@{clean || invite.suggested_handle}</span>
                <span className="pill ok">{invite.handle_available ? "Available" : "Customize after sign-in"}</span>
              </div>
              <div className="tiny mt4">You can keep @{invite.suggested_handle} or pick another handle after signing in.</div>
            </div>

            <ErrorLine error={err} />

            <div className="mt24">
              <button
                type="button"
                className="btn dark full"
                disabled={busy || !verification.ready}
                onClick={onGoogleContinue}
                data-testid="claim-google-btn"
              >
                Continue with Google to claim
              </button>
            </div>
            {verification.element}
            <p className="tiny center mt10">
              Sent to {invite.email_hint} · No paid plan needed to watch or download your clips
            </p>

            {devOpen && dev.enabled && (
              <form className="mt20 stack" style={{ gap: 10 }} onSubmit={onDevSignIn} data-testid="claim-dev-login">
                <label className="tiny">
                  Email (dev)
                  <input className="field mt6" value={devEmail} onChange={(e) => setDevEmail(e.target.value)} />
                </label>
                <label className="tiny">
                  Name (dev)
                  <input className="field mt6" value={devName} onChange={(e) => setDevName(e.target.value)} />
                </label>
                <button className="btn full" type="submit" disabled={busy} data-testid="claim-dev-submit">
                  Sign in (dev)
                </button>
              </form>
            )}
          </>
        ) : (
          <div className="mt20" data-testid="claim-signed-in">
            <div className="tiny">Signed in as <b>{me.user.email}</b></div>
            {!invite.handle_available && !me.user.handle && (
              <div className="notice mt12" data-testid="claim-handle-collision">
                <div className="tiny">
                  @{invite.suggested_handle} was taken before you claimed this invite. Pick an available handle below — your clips will still attach to your account.
                </div>
              </div>
            )}
            <label className="tiny mt16" style={{ display: "block" }} htmlFor="claim-handle-input">
              Confirm your @handle
            </label>
            <div className="handle-field mt6">
              <span className="handle-at">@</span>
              <input
                id="claim-handle-input"
                className="field handle-in focus"
                value={handle}
                onChange={(e) => setHandle(e.target.value.replace(/^@+/, "").toLowerCase())}
                maxLength={24}
                data-testid="claim-handle-input"
              />
            </div>
            {check && (
              check.valid && check.available ? (
                <p className="tiny mt6" style={{ color: "var(--ok)" }}>
                  Available · Your page will be deepthird.com/u/{clean}
                </p>
              ) : (
                <p className="tiny bad mt6">{check.message || `@${clean} is unavailable.`}</p>
              )
            )}
            {check && check.suggestions.length > 0 && (
              <div className="handle-sugs">
                {check.suggestions.map((s) => (
                  <button key={s} type="button" className="handle-sug" onClick={() => setHandle(s)}>
                    @{s}
                  </button>
                ))}
              </div>
            )}

            {needsEmailConfirm && (
              <div className="notice mt16" data-testid="claim-email-mismatch">
                <div className="med">This invite was sent to {invite.email_hint}</div>
                <p className="tiny mt4">
                  You are signed in as <b>{me.user.email}</b>. Confirm to claim these clips on this Google account.
                </p>
                <div className="row mt12" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className="btn dark sm"
                    disabled={busy}
                    onClick={() => doClaim(true)}
                    data-testid="claim-confirm-mismatch"
                  >
                    Claim as {me.user.email}
                  </button>
                  <button
                    type="button"
                    className="btn ghost sm"
                    disabled={busy}
                    onClick={() => signOut()}
                  >
                    Switch account
                  </button>
                </div>
              </div>
            )}

            {emailForbidden && (
              <div className="notice mt16" data-testid="claim-email-forbidden">
                <div className="med bad">Manager invites must be claimed by {invite.email_hint}</div>
                <p className="tiny mt4">
                  You are signed in as <b>{me.user.email}</b>. Sign out and sign in with the invited address to claim manager access.
                </p>
                <div className="mt12">
                  <button type="button" className="btn ghost sm" onClick={() => signOut()}>
                    Sign out & switch account
                  </button>
                </div>
              </div>
            )}

            <ErrorLine error={err} />

            {!needsEmailConfirm && !emailForbidden && (
              <div className="mt24">
                <button
                  type="button"
                  className="btn dark full"
                  disabled={busy || !clean || (check !== null && (!check.valid || !check.available))}
                  onClick={() => doClaim(false)}
                  data-testid="claim-submit-btn"
                >
                  {busy
                    ? "Claiming…"
                    : invite.match
                      ? `Claim ${invite.match.clip_count} clips as @${clean}`
                      : `Join ${invite.team.name} as @${clean}`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Shell>
  );
}
